import {
	Scene,
	Observable,
	TransformNode,
	Mesh,
	AbstractMesh,
	MeshBuilder,
	SceneLoader,
	Tools,
	Vector3,
	PBRMaterial,
} from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import ObservablesManager from './ObservablesManager';
import LineBuilder from './MeshFactory/LineBuilder';
import TextBuilder from './MeshFactory/TextBuilder';
import { MeshAssembly } from '../MeshAssembly';
import { GltfLoaderConfiguration } from '../configuration/interfaces/GltfLoaderConfiguration';
import { BoxMeshConfiguration } from '../configuration/interfaces/DefaultMeshConfiguration';
import { AssetsPackage } from 'src/interfaces';

export default class MeshFactory {
	private scene: Scene;
	private observablesManager: ObservablesManager;
	private assemblies: Map<string, MeshAssembly>;

	private readonly assembliesHolder: MeshAssembly;

	public textBuilder: TextBuilder;
	public lineBuilder: LineBuilder;

	public environmentIntensity: number;

	constructor(scene: Scene, observablesManager: ObservablesManager) {
		this.scene = scene;
		this.observablesManager = observablesManager;
		this.assemblies = new Map<string, MeshAssembly>();

		this.assembliesHolder = this.createEmpty('assemblies');

		this.textBuilder = new TextBuilder(this);
		this.lineBuilder = new LineBuilder(this, scene);
	}

	/**
	 * Will notify when the mesh was created.
	 */
	public get onMeshCreated(): Observable<Mesh> {
		return this.observablesManager.onMeshCreatedObservable;
	}

	/**
	 * Load gltf model.
	 */
	public async loadGltf(
		path: string,
		configuration?: GltfLoaderConfiguration
	): Promise<MeshAssembly> {
		const directory = Tools.GetFolderPath(path);
		const fileName = Tools.GetFilename(path);

		const {
			isVisible = false,
			rootName = '__root__',
			scaling = 10,
		}: GltfLoaderConfiguration = configuration || {};

		const assets = await SceneLoader.ImportMeshAsync(null, directory, fileName, this.scene);

		let root: AbstractMesh | null = null;

		const promises: Promise<unknown>[] = [];
		assets.meshes.forEach((mesh) => {
			mesh.isPickable = false;
			// Make sure to render at least once
			mesh.isVisible = true;
			if (mesh.name === rootName) {
				root = mesh;
				const scale = mesh.scaling;
				mesh.scaling = new Vector3(scale.x * scaling, scale.y * scaling, scale.z * scaling);
			}

			if (mesh.subMeshes && mesh.subMeshes.length > 0) {
				// setup listeners to the first render
				promises.push(
					new Promise<void>((resolve) => {
						(mesh as Mesh).onBeforeRenderObservable.addOnce(() => {
							setTimeout(() => resolve(), 10);
						});
					})
				);
			}
		});

		if (!root) {
			throw new Error('Root is not found in gltf file!');
		}

		const assembly = this.createEmpty(path);
		this.assemblies.set(path, assembly);
		(root as AbstractMesh).parent = assembly.transformNode;

		// Waiting for all meshes to be rendered once
		try {
			await Promise.all(promises);
		} catch (e) {
			// ignoring errors
		}

		// set the isVisible status after the first render
		assets.meshes.forEach((mesh) => (mesh.isVisible = isVisible));

		assembly.parent = this.assembliesHolder;
		return assembly;
	}

	/**
	 * Load assets package.
	 */
	public async loadAssetsPackage(
		assetsPackage: AssetsPackage,
		configuration?: GltfLoaderConfiguration
	): Promise<MeshAssembly[]> {
		const { rootName = '__root__', scaling = 10 }: GltfLoaderConfiguration = configuration || {};

		const materialsMap = new Map<string, PBRMaterial>();
		const materialsMeshes = new Array<AbstractMesh>();

		const geometryMap = new Map<string, AbstractMesh>();

		const { materialsUrl, geometryUrls, map } = assetsPackage;

		const materialsDirectory = Tools.GetFolderPath(materialsUrl);
		const materialsFileName = Tools.GetFilename(materialsUrl);

		// load materials
		const promises = [
			SceneLoader.ImportMeshAsync(null, materialsDirectory, materialsFileName, this.scene)
				.then((materialsAssets) => {
					const root = materialsAssets.meshes.find((mesh) => mesh.name == rootName);
					if (root) {
						const materialMeshes = root.getChildMeshes(true);

						materialMeshes.forEach((mesh) => {
							mesh.isPickable = false;
							mesh.isVisible = false;
							if (mesh.material) {
								const material = mesh.material as PBRMaterial;
								const name = mesh.name.toLowerCase();
								materialsMap.set(name, material);
							}
						});
					}
				}) // eslint-disable-next-line @typescript-eslint/no-unused-vars
				.catch((e) => {
					// ignore error
				}),
		];

		// load geometry
		geometryUrls.forEach((url) => {
			const directory = Tools.GetFolderPath(url);
			const fileName = Tools.GetFilename(url);

			promises.push(
				SceneLoader.ImportMeshAsync(null, directory, fileName, this.scene)
					.then((geometryAssets) => {
						const root = geometryAssets.meshes.find((mesh) => mesh.name == rootName);
						geometryAssets.meshes.forEach((mesh) => (mesh.isPickable = false));
						if (root) {
							const scale = root.scaling;
							root.scaling = new Vector3(scale.x * scaling, scale.y * scaling, scale.z * scaling);
							const name = fileName.split('.')[0].toLowerCase();
							geometryMap.set(name, root);
						} else {
							throw new Error('Root is not found in gltf file!');
						}
					})
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					.catch((e) => {
						// ignore error
					})
			);
		});
		await Promise.all(promises);

		const renderPromises: Promise<unknown>[] = [];

		// create assemblies and register them as urls
		map.forEach((mapping, key) => {
			const geometry = geometryMap.get(mapping.geometry.toLowerCase());
			const material = materialsMap.get(mapping.material.toLowerCase());

			if (geometry) {
				const assembly = this.createEmpty(key);
				const mesh = geometry.clone(key, assembly.transformNode) as AbstractMesh;
				const children = mesh.getChildren();

				const fabric = children.find((child) => child.name == key + '.fabric') as AbstractMesh;

				if (fabric && material) {
					fabric.material = material;
				}

				const childMeshes = mesh.getChildMeshes();
				childMeshes.forEach((mesh) => {
					if (mesh.subMeshes && mesh.subMeshes.length > 0) {
						renderPromises.push(
							new Promise<void>((resolve) => {
								(mesh as Mesh).onBeforeRenderObservable.addOnce(() => {
									setTimeout(() => resolve(), 10);
								});
							})
						);
					}
				});

				this.assemblies.set(key, assembly);
				assembly.shouldRender = true;
			}
		});
		// Waiting for all meshes to be rendered once
		try {
			await Promise.all(renderPromises);
		} catch (e) {
			// ignoring errors
		}
		// remove geometry and materials meshes to use less memory
		geometryMap.forEach((m) => m.dispose());
		materialsMeshes.forEach((m) => m.dispose());

		// hide assemblies
		this.assemblies.forEach((a) => (a.shouldRender = false));

		return Array.from(this.assemblies.values());
	}

	/**
	 * Create empty.
	 */
	public createEmpty(name?: string): MeshAssembly {
		const transformNode = new TransformNode(name ? name : '', this.scene);
		return new MeshAssembly(transformNode, name);
	}

	/**
	 * Clone.
	 */
	public cloneAssembly(id: string): MeshAssembly {
		const assembly = this.assemblies.get(id);
		if (assembly) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const transformNode = assembly.transformNode.clone(id, null as any);

			if (transformNode) {
				// Apply environmentIntensity;
				if (this.environmentIntensity) {
					const childMeshes = transformNode.getChildMeshes();
					childMeshes.forEach((mesh) => {
						if (mesh.material) {
							const material = mesh.material as PBRMaterial;
							if (material.environmentIntensity) {
								material.environmentIntensity = this.environmentIntensity;
							}
						}
					});
				}

				return new MeshAssembly(transformNode, assembly.name);
			}
		}

		throw 'MeashFactory: cloneAssembly(): assembly does not exist.';
	}

	/**
	 * Create box.
	 */
	public createBox(configuration: BoxMeshConfiguration, id: string): MeshAssembly {
		const {
			isVisible = false,
			width,
			height,
			depth,
		}: BoxMeshConfiguration = configuration || {
			width: 5,
			height: 5,
			depth: 5,
		};
		const assembly = this.createEmpty(id);
		const box = MeshBuilder.CreateBox('box', { width, height, depth }, this.scene);
		box.isVisible = isVisible || false;
		box.isPickable = false;
		box.parent = assembly.transformNode;
		return assembly;
	}

	/**
	 * Create ground.
	 */
	public createGround(tag: string): MeshAssembly {
		const options = {
			width: 500,
			height: 500,
		};
		const assembly = this.createEmpty(tag);
		const ground = MeshBuilder.CreateGround(tag, options, this.scene);
		ground.isPickable = true;
		ground.visibility = 0;
		ground.receiveShadows = true;
		ground.parent = assembly.transformNode;
		return assembly;
	}

	public getMeshAssembly(id: string): MeshAssembly {
		const assembly = this.assemblies.get(id);
		if (assembly) {
			return assembly;
		}

		throw 'MeshFactory: getMeshAssembly(): assembly does not exist.';
	}
}
